import { useEffect } from 'react';
import { useStore } from 'zustand';
import { useAssemblyStore } from '../stores/AssemblyStore';
import { useFoundrySelectionStore } from '../stores/foundrySelection.store';
import { GlobalAssemblyItem } from '../api/protosCompiled/globalAssembly/globalAssembly_pb';
import { TakeoffAssemblyItem } from '../types/AssemblyItemType';

/**
 * Hook to preload BuildFoundry items when assemblies are loaded
 * This ensures that when a user opens an assembly with BuildFoundry items,
 * all related items from the same category are already available in the dropdown
 */
export function usePreloadFoundryItems() {
  const currentGlobalAssembly = useStore(useAssemblyStore, state => state.currentGlobalAssembly);
  const currentTakeoffAssembly = useStore(useAssemblyStore, state => state.currentTakeoffAssembly);
  const preloadFoundryItemsForAssembly = useStore(
    useFoundrySelectionStore,
    state => state.preloadFoundryItemsForAssembly
  );

  useEffect(() => {
    const assembly = currentGlobalAssembly || currentTakeoffAssembly;

    if (assembly?.itemsList && assembly.itemsList.length > 0) {
      // Type the items as a union type to handle both global and takeoff assemblies
      const items = assembly.itemsList as (GlobalAssemblyItem.AsObject | TakeoffAssemblyItem)[];

      // Check if there are any BuildFoundry items in the assembly
      const foundryItems = items.filter(
        (item: GlobalAssemblyItem.AsObject | TakeoffAssemblyItem) =>
          (item as any)?.isfoundryitem === true ||
          (item as any)?.attribute?.attributevaluename?.includes('Foundry Item')
      );

      if (foundryItems.length > 0) {
        console.log(
          `🔧 Found ${foundryItems.length} BuildFoundry items in assembly: ${assembly.assemblyname}`
        );
        console.log(
          '🔧 Foundry items:',
          foundryItems.map((item: GlobalAssemblyItem.AsObject | TakeoffAssemblyItem) => ({
            id: (item as any)?.itemid,
            name: (item as any)?.itemname,
            isFoundryItem: (item as any)?.isfoundryitem,
            attributeName: (item as any)?.attribute?.attributevaluename,
          }))
        );

        // Set category paths for foundry items (from stored data or fallback)
        const { setFoundryCategoryForItem } = useFoundrySelectionStore.getState();

        // Process items and fetch category paths for those without stored paths
        const itemsNeedingCategoryPaths: (GlobalAssemblyItem.AsObject | TakeoffAssemblyItem)[] = [];

        foundryItems.forEach((item: GlobalAssemblyItem.AsObject | TakeoffAssemblyItem) => {
          const storedPath = (item as any)?.foundrycategorypath;
          if (storedPath) {
            console.log(
              `🔧 Using stored category path for item ${(item as any)?.itemname}: ${storedPath}`
            );
            setFoundryCategoryForItem((item as any)?.itemid, storedPath);
          } else {
            // Mark item as needing category path fetch
            itemsNeedingCategoryPaths.push(item);
          }
        });

        // Fetch category paths for items without stored paths
        if (itemsNeedingCategoryPaths.length > 0) {
          console.log(
            `🔧 Fetching category paths for ${itemsNeedingCategoryPaths.length} items without stored paths`
          );

          const fetchPromises = itemsNeedingCategoryPaths.map(async (item: any) => {
            try {
              const { BuildFoundryService } = await import('../api/BuildFoundryService');
              const itemDetails = await BuildFoundryService.getFoundryItemById(item.itemid);

              if (itemDetails.data?.categoryId) {
                console.log(
                  `🔧 Fetched category path for item ${item.itemname}: ${itemDetails.data.categoryId}`
                );
                setFoundryCategoryForItem(item.itemid, itemDetails.data.categoryId);
                return itemDetails.data.categoryId;
              } else {
                console.warn(`🔧 No category path found for item ${item.itemname}`);
                return null;
              }
            } catch (err) {
              console.error(`🔧 Failed to fetch category path for item ${item.itemname}:`, err);
              return null;
            }
          });

          Promise.all(fetchPromises).then(() => {
            // After all category paths are fetched, preload the foundry items
            preloadFoundryItemsForAssembly(assembly.itemsList);
          });
        } else {
          // If no items need category paths, proceed directly with preloading
          preloadFoundryItemsForAssembly(assembly.itemsList);
        }
      }
    }
  }, [currentGlobalAssembly, currentTakeoffAssembly, preloadFoundryItemsForAssembly]);
}
